import * as z from "zod";

/**
 * `attachments` / `audios` stay `z.array(z.any())` on purpose: typing them as
 * full recursive attachment resource shapes makes TanStack Form's `DeepKeys`
 * hit the TS2589 instantiation limit. The *external* prop types
 * (`ChatComposerValues`) are precise; only the schema is loose.
 *
 * `FormAttachment` is the flat replacement; moving these remaining composers
 * onto it is the backlog item "retire AttachmentWithStatus".
 */

/**
 * The over-limit message. One home, because it is both the schema's error and
 * the text the composer shows under the box.
 */
const tooLongMessage = (maxCharacters: number) =>
  `Message cannot exceed ${maxCharacters} characters.`;

/**
 * Code POINTS, matching how the platform counts the same body
 * (`Array.from(text).length` in `channel-message.dto.ts`).
 *
 * `.max()` would count UTF-16 units, which is exactly half the server's limit for
 * every non-BMP character — so a body of native emoji could be refused by the
 * client while the server considered it well within range.
 */
export function countMessageCharacters(content: string): number {
  return Array.from(content.trim()).length;
}

/**
 * Why this content cannot be sent on a length basis, or `undefined` when it can.
 *
 * Exported because the composer shows it live: the send button is disabled while
 * the body is over the limit, and a disabled button never reaches a submit
 * attempt, so the form's own error display would never be triggered to explain
 * itself.
 */
export function getMessageLengthError(
  content: string,
  maxCharacters?: number,
): string | undefined {
  if (!maxCharacters) return undefined;
  if (countMessageCharacters(content) <= maxCharacters) return undefined;
  return tooLongMessage(maxCharacters);
}

export function buildChatComposerSchema(options?: {
  maxCharacters?: number;
  /** An empty message is valid — it carries something besides the box. */
  allowEmpty?: boolean;
}) {
  const maxCharacters = options?.maxCharacters;
  const content = maxCharacters
    ? z
        .string()
        .trim()
        .refine((value) => countMessageCharacters(value) <= maxCharacters, {
          message: tooLongMessage(maxCharacters),
        })
    : z.string().trim();

  return z
    .object({
      content,
      attachments: z.array(z.any()),
      audios: z.array(z.any()),
    })
    .refine(
      (data) =>
        options?.allowEmpty === true ||
        data.content.length > 0 ||
        data.attachments.length > 0 ||
        data.audios.length > 0,
      {
        message: "Content or attachments are required.",
        path: ["content"],
      },
    );
}

export const EMPTY_CHAT_COMPOSER_VALUES = {
  content: "",
  attachments: [] as any[],
  audios: [] as any[],
};
